import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import qs from 'qs';
import {useClient} from 'context/auth-context';
import {
    AMNIS_BANK_ACCOUNTS,
    CONTACT_BANK_ACCOUNTS_CACHE,
    CONTACT_DETAILS_CACHE,
    CONTACTS_LIST_CACHE,
} from 'constants/cache';
import {IAmnisBankAccount, IBankAccount, IBankAccountCreate} from 'types/api';

const useCreateBankAccountRequest = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<IBankAccount, unknown, IBankAccountCreate>({
        mutationFn: data => {
            return client('/web_api/bank_accounts', {
                data,
                method: 'POST',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [AMNIS_BANK_ACCOUNTS],
            });
            queryClient.invalidateQueries({
                queryKey: [CONTACT_BANK_ACCOUNTS_CACHE],
            });
        },
    });
};

const useEditBankAccount = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        IBankAccount,
        unknown,
        Pick<IBankAccount, '@id' | 'name'>
    >({
        mutationFn: ({'@id': id, ...rest}) => {
            return client(id, {
                data: rest,
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [AMNIS_BANK_ACCOUNTS],
            });
            queryClient.invalidateQueries({
                queryKey: [CONTACT_BANK_ACCOUNTS_CACHE],
            });
        },
    });
};
export const IBAN_ERRORS = [
    'own_iban',
    'amnis_iban_closed',
    'amnis_iban',
    'different_peer',
    'already_peer',
] as const;
export type IbanError = (typeof IBAN_ERRORS)[number];
export type IbanInfo = {
    bank: string;
    bankCity: string;
    countryIso: string;
    passed: boolean;
    result: string;
    swift: string;
};

const useCheckIban = (iban: string) => {
    const client = useClient();
    return useMutation<
        IbanInfo,
        IbanError | (Omit<IbanInfo, 'result'> & {result: 'failed'}),
        string
    >({
        mutationFn: iban =>
            client(`/web_api/iban_check/${iban}`, {
                method: 'GET',
            }),
    });
};

export interface IAmnisBank {
    '@id': string;
    '@type': string;
    address: string;
    id: number;
    name: string;
    swift: string;
}

const useGetAmnisBankAccounts = (currencies: string[]) => {
    const client = useClient();
    const currenciesString = qs.stringify(
        {currency: currencies},
        {
            encode: false,
        },
    );

    return useQuery<IAmnisBankAccount[]>({
        queryKey: [AMNIS_BANK_ACCOUNTS, {currenciesString}],
        queryFn: () =>
            client(`/web_api/amnis_bank_accounts?${currenciesString}`, {}),

        enabled: !!currencies?.length,
    });
};

interface IUseConnectPeerBankAccountRequest {
    contact: string;
    iban: string;
}

const useConnectPeerBankAccount = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<string, unknown, IUseConnectPeerBankAccountRequest>({
        mutationFn: data =>
            client('/web_api/bank_account_connect_peer', {
                method: 'POST',
                data,
            }),
        onSuccess: (_, requestData) => {
            queryClient.invalidateQueries({
                queryKey: [
                    CONTACT_DETAILS_CACHE,
                    {id: requestData.contact?.split('/').pop()},
                ],
            });
            queryClient.invalidateQueries({queryKey: [CONTACTS_LIST_CACHE]});
        },
    });
};

export {
    useCreateBankAccountRequest,
    useCheckIban,
    useGetAmnisBankAccounts,
    useEditBankAccount,
    useConnectPeerBankAccount,
};
